/**
 * Utility functions for proper useEffect cleanup to prevent memory leaks
 */

/**
 * Creates a cleanup function for event listeners
 */
export const createEventListenerCleanup = (
  element: EventTarget,
  event: string,
  handler: EventListener,
  options?: AddEventListenerOptions
) => {
  element.addEventListener(event, handler, options);
  
  return () => {
    element.removeEventListener(event, handler);
  };
};

/**
 * Creates a cleanup function for keyboard event listeners with proper typing
 */
export const createKeyboardEventCleanup = (
  element: EventTarget,
  handler: (event: KeyboardEvent) => void,
  options?: AddEventListenerOptions
) => {
  const eventListener = handler as EventListener;
  element.addEventListener('keydown', eventListener, options);
  
  return () => {
    element.removeEventListener('keydown', eventListener);
  };
};

/**
 * Creates a cleanup function for intervals
 */
export const createIntervalCleanup = (callback: () => void, delay: number) => {
  const intervalId = setInterval(callback, delay);
  
  return () => {
    clearInterval(intervalId);
  };
};

/**
 * Creates a cleanup function for timeouts
 */
export const createTimeoutCleanup = (callback: () => void, delay: number) => {
  const timeoutId = setTimeout(callback, delay);
  
  return () => {
    clearTimeout(timeoutId);
  };
};

/**
 * Creates a cleanup function for animation frames
 */
export const createAnimationFrameCleanup = (callback: FrameRequestCallback) => {
  const rafId = requestAnimationFrame(callback);
  
  return () => {
    cancelAnimationFrame(rafId);
  };
};

/**
 * Combines multiple cleanup functions into a single cleanup function
 */
export const combineCleanups = (...cleanups: (() => void)[]) => {
  return () => {
    cleanups.forEach(cleanup => {
      try {
        cleanup();
      } catch (error) {
        console.error('Error during cleanup:', error);
      }
    });
  };
};

/**
 * Creates a debounced cleanup function that prevents rapid cleanup/setup cycles
 */
export const createDebouncedCleanup = (
  setup: () => (() => void),
  delay: number = 100
) => {
  let timeoutId: NodeJS.Timeout | null = null;
  let currentCleanup: (() => void) | null = null;
  
  const debouncedSetup = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    
    timeoutId = setTimeout(() => {
      if (currentCleanup) {
        currentCleanup();
      }
      currentCleanup = setup();
      timeoutId = null;
    }, delay);
  };
  
  const cleanup = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    if (currentCleanup) {
      currentCleanup();
      currentCleanup = null;
    }
  };
  
  return { setup: debouncedSetup, cleanup };
};

/**
 * Hook for managing AbortController cleanup
 */
export const createAbortControllerCleanup = () => {
  const controller = new AbortController();
  
  return {
    signal: controller.signal,
    cleanup: () => {
      controller.abort();
    }
  };
};

/**
 * Creates a cleanup function for DOM elements
 */
export const createDOMElementCleanup = (element: HTMLElement) => {
  return () => {
    if (element.parentNode) {
      element.parentNode.removeChild(element);
    }
  };
};

/**
 * Creates a cleanup function for observers (IntersectionObserver, MutationObserver, etc.)
 */
export const createObserverCleanup = (observer: { disconnect: () => void }) => {
  return () => {
    observer.disconnect();
  };
};